import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { AioButtonComponent, AioInputFieldComponent, AioPhoneFieldComponent, AioSelectComponent, type AioPhoneCountry } from '@aio/ui';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Lead, LeadUpdateRequest } from '../../../core/models/lead.model';
import { COUNTRY_CODE_OPTIONS, getCountryFullName, getCountryPhoneCode } from '../../constants/countries.constants';
import {
  extensionForWorkPhoneType,
  formatPhoneNumberForInput as formatPhoneNumberForInputDisplay,
  normalizeUsPhoneInputDigits
} from '../../utils/phone-number.util';
import { ScrollThroughDirective } from '../../directives/scroll-through.directive';

@Component({
  selector: 'app-contact-information-card',
  standalone: true,
  imports: [
    ScrollThroughDirective,
    AioButtonComponent, AioInputFieldComponent, AioPhoneFieldComponent, CommonModule, FormsModule, AioSelectComponent],
  templateUrl: './contact-information-card.component.html',
  styleUrls: ['./contact-information-card.component.scss']
})
export class ContactInformationCardComponent implements OnChanges, OnDestroy {
  @Input() lead?: Lead;
  @Output() editClick = new EventEmitter<void>();
  @Output() expandClick = new EventEmitter<void>();
  @Output() saveClick = new EventEmitter<LeadUpdateRequest>();
  @Output() cancelClick = new EventEmitter<void>();

  expandedContacts: Set<number> = new Set();
  isExpanded = false; // Local expand state for the card
  isEditing = false; // Edit state for the card

  // Store original participants to detect deletions
  originalParticipantIds: Set<number> = new Set();

  // Edit form values - array for all contacts
  editContacts: Array<{
    participantId?: number;
    contactId?: number | null; // Store contactId for converted contacts
    firstName: string;
    lastName: string;
    email: string;
    affiliation: string;
    phoneNumbers: Array<{
      phoneNumber: string;
      phoneExtension: string;
      phoneNumberType: string;
      countryCode: string;
    }>;
    countryCode: string;
    isPrimary: boolean;
    isNew?: boolean; // Flag to identify new contacts
  }> = [];

  showPhoneLimitMessage = false;
  phoneLimitMessageTimeout: any = null;

  onEditClick(): void {
    if (!this.isEditing) {
      // Auto-expand the card when editing starts
      if (!this.isExpanded) {
        this.isExpanded = true;
      }
      this.startEditing();
    }
  }

  onExpandClick(): void {
    this.isExpanded = !this.isExpanded;
    this.expandClick.emit();
  }

  /**
   * Validate phone number fields - if any field is filled, type and phone number must be filled (country is +1 via API).
   */
  private validatePhoneNumberFields(phone: any, contactIndex: number, phoneIndex: number): { isValid: boolean; error: string | null } {
    if (!phone) {
      return { isValid: true, error: null };
    }

    const phoneTypeStr = phone.phoneNumberType ? String(phone.phoneNumberType).trim() : '';
    const phoneNumberStr = phone.phoneNumber ? String(phone.phoneNumber).trim() : '';

    if (phoneNumberStr === '') {
      if (phoneTypeStr !== '') {
        return { isValid: false, error: 'Phone type and phone number are required when entering phone information' };
      }
      return { isValid: true, error: null };
    }

    if (!phoneTypeStr) {
      return { isValid: false, error: 'Phone type and phone number are required when entering phone information' };
    }

    return { isValid: true, error: null };
  }

  /**
   * Validate all phone numbers for all contacts
   */
  private validateAllPhoneNumbers(): boolean {
    let allValid = true;
    
    this.editContacts.forEach((contact, contactIndex) => {
      if (!contact.phoneNumbers || !Array.isArray(contact.phoneNumbers)) {
        return;
      }

      contact.phoneNumbers.forEach((phone, phoneIndex) => {
        const validation = this.validatePhoneNumberFields(phone, contactIndex, phoneIndex);
        if (!validation.isValid) {
          allValid = false;
        }
      });
    });

    return allValid;
  }

  /**
   * Validate email format
   * Returns true if email is valid format, false if empty or invalid
   */
  private isValidEmail(email: string | undefined): boolean {
    if (!email || email.trim() === '') {
      return false; // Email is required, empty is invalid
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  /**
   * Check if save button should be enabled
   * Save is enabled only when:
   * 1. Every contact has both first and last name filled
   * 2. At least one contact has a non-empty email field
   * 3. All emails are valid format
   * 4. All phone numbers are either completely empty OR have both type and number
   */
  get isSaveEnabled(): boolean {
    if (this.editContacts.length === 0) {
      return false;
    }

    const allContactsHaveNames = this.editContacts.every((contact) => {
      const firstName = contact.firstName ? String(contact.firstName).trim() : '';
      const lastName = contact.lastName ? String(contact.lastName).trim() : '';
      return firstName !== '' && lastName !== '';
    });

    if (!allContactsHaveNames) {
      return false;
    }
    
    // Check that at least one contact has a non-empty email
    const hasAtLeastOneEmail = this.editContacts.some(contact => {
      const email = contact.email ? String(contact.email).trim() : '';
      return email !== '';
    });
    
    if (!hasAtLeastOneEmail) {
      return false; // Disable save if no contact has an email
    }
    
    // Check all contacts for valid email formats
    for (const contact of this.editContacts) {
      const email = contact.email ? String(contact.email).trim() : '';
      // Only validate format if email is not empty
      if (email !== '' && !this.isValidEmail(email)) {
        return false; // Disable save if any email is invalid format
      }
    }
    
    // Check all contacts
    for (const contact of this.editContacts) {
      if (!contact.phoneNumbers || !Array.isArray(contact.phoneNumbers)) {
        continue;
      }

      // Check all phone numbers for this contact
      for (const phone of contact.phoneNumbers) {
        const phoneTypeStr = phone.phoneNumberType ? String(phone.phoneNumberType).trim() : '';
        const phoneNumberStr = phone.phoneNumber ? String(phone.phoneNumber).trim() : '';
        const hasType = phoneTypeStr !== '';
        const hasPhoneNumber = phoneNumberStr !== '';
        const filledCount = [hasType, hasPhoneNumber].filter(Boolean).length;
        if (filledCount === 0) {
          continue;
        }
        if (filledCount === 1) {
          return false;
        }
      }
    }
    
    return true;
  }

  onSaveClick(): void {
    if (!this.lead) return;

    // Validate all phone numbers before saving
    const isValid = this.validateAllPhoneNumbers();
    
    if (!isValid) {
      // Validation failed - don't save
      console.warn('⚠️ Phone number validation failed. Please complete all phone number fields.');
      return;
    }

    // Get primary contact for root level fields
    const primaryContact = this.editContacts.find(c => c.isPrimary) || this.editContacts[0];
    
    // Get first phone number for primary contact
    const firstPhone = primaryContact.phoneNumbers && primaryContact.phoneNumbers.length > 0 
      ? primaryContact.phoneNumbers[0] 
      : { phoneNumber: '', phoneExtension: '' };

    // Include all existing lead fields to prevent data loss
    // Note: Do not include projectValue - this card doesn't edit it, and including it
    // causes incorrect history log entries (e.g., "value changed from '0.00' to '0'")
    const updateRequest: LeadUpdateRequest = {
      id: this.lead.id,
      title: this.lead.title,
      description: this.lead.description,
      // Update primary contact fields from editContacts
      firstName: primaryContact.firstName,
      lastName: primaryContact.lastName,
      email: primaryContact.email,
      phoneNumber: firstPhone.phoneNumber,
      phoneExtension: firstPhone.phoneExtension,
      // Preserve all other lead fields
      middleName: this.lead.middleName,
      projectTypeId: this.lead.projectTypeId,
      // projectValue is NOT included - this card doesn't edit it
      temperatureTypeId: this.lead.temperatureTypeId,
      projectOwnerId: this.lead.projectOwnerId,
      referralChannelId: this.lead.referralChannelId ?? undefined,
      leadOriginId: this.lead.leadOriginId,
      // Preserve all address fields
      street1: this.lead.street1,
      street2: this.lead.street2,
      city: this.lead.city,
      state: this.lead.state,
      zip: this.lead.zip,
      countryCode: this.lead.countryCode,
      country: this.lead.country,
      // Preserve other fields
      isFlagged: this.lead.isFlagged,
      isArchived: this.lead.isArchived,
      isDeleted: this.lead.isDeleted
    };

    // Add participant data as a custom property to indicate this is from contact card
    // Format participants to match the structure used in saveParticipantsUnified
    (updateRequest as any).isContactCardSave = true;
    (updateRequest as any).participants = this.editContacts.map(contact => {
      // Convert phone numbers to match the format expected by mapPhoneNumbersToParticipantAPI
      const phoneNumbers = (contact.phoneNumbers || [])
        .filter((phone: any) => {
          const hasType = phone.phoneNumberType && phone.phoneNumberType.trim() !== '';
          const hasPhoneNumber = phone.phoneNumber && phone.phoneNumber.trim() !== '';
          return hasType && hasPhoneNumber;
        })
        .map((phone: any) => ({
          phoneNumber: phone.phoneNumber || '',
          phoneExtension: extensionForWorkPhoneType(phone.phoneNumberType, phone.phoneExtension),
          phoneType: phone.phoneNumberType || '',
          phoneCountryCode: phone.countryCode || 'United States'
        }));

      // Convert country code from full name to code if needed for participant countryCode
      // Only use actual values, no defaults
      let contactCountryCode = contact.countryCode ? String(contact.countryCode).trim() : '';
      if (contactCountryCode !== '' && contactCountryCode.length <= 2) {
        // It's already a code, convert to full name
        const fullName = getCountryFullName(contactCountryCode);
        // Only use converted name if it exists and is different
        contactCountryCode = fullName && fullName !== contactCountryCode ? fullName : contactCountryCode;
      }

      return {
        participantId: contact.participantId,
        contactId: contact.contactId || null, // Use contactId from editContacts (already stored in startEditing)
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        affiliation: contact.affiliation || '',
        phoneNumbers: phoneNumbers,
        countryCode: contactCountryCode, // Full name for consistency
        country: contactCountryCode, // Full name
        isPrimaryParticipant: contact.isPrimary || false, // Use isPrimaryParticipant to match working code
        isNew: contact.isNew || !contact.participantId || contact.participantId <= 0,
        // Add address fields (empty for contact card since it doesn't have address fields)
        city: '',
        streetAddress: '',
        streetAddress2: '',
        zipCode: '',
        state: ''
      };
    });
    // Track deleted participants
    (updateRequest as any).deletedParticipantIds = Array.from(this.originalParticipantIds).filter(id => 
      !this.editContacts.some(c => c.participantId === id)
    );

    this.saveClick.emit(updateRequest);
    // Collapse card and exit edit mode after save
    this.isEditing = false;
    this.isExpanded = false;
  }

  onCancelClick(): void {
    this.isEditing = false;
    this.isExpanded = false; // Collapse card when canceling
    this.cancelClick.emit();
  }

  private startEditing(): void {
    this.isEditing = true;
    // Store original participant IDs to detect deletions
    this.originalParticipantIds.clear();
    const contacts = this.getContacts();
    contacts.forEach(contact => {
      const contactAny = contact as any;
      if (contactAny.participantId && contactAny.participantId > 0) {
        this.originalParticipantIds.add(contactAny.participantId);
      }
    });

    // Initialize form values from all contacts
    this.editContacts = contacts.map(contact => {
      // Type-safe property access - handle both participant and fallback contact types
      const contactAny = contact as any;
      
      // Build phone numbers array
      const phoneNumbers: Array<{
        phoneNumber: string;
        phoneExtension: string;
        phoneNumberType: string;
        countryCode: string;
      }> = [];

      // First, check if contact has phoneNumbers array (new structure)
      if (contactAny.phoneNumbers && Array.isArray(contactAny.phoneNumbers) && contactAny.phoneNumbers.length > 0) {
        contactAny.phoneNumbers.forEach((phone: any) => {
          // Get phone type - could be phoneType or phoneNumberType, but no default
          const phoneType = phone.phoneType || phone.phoneNumberType || '';
          
          // Get country code - could be phoneCountryCode (full name) or countryCode, but no default
          let countryCode = phone.phoneCountryCode || phone.countryCode || '';
          
          // If it's a 2-letter code, convert to full name
          if (countryCode && countryCode.length <= 2) {
            countryCode = getCountryFullName(countryCode) || '';
          }
          
          const mappedType = phoneType ? this.mapPhoneType(phoneType) : '';
          phoneNumbers.push({
            phoneNumber: phone.phoneNumber || '',
            phoneExtension: extensionForWorkPhoneType(mappedType, phone.phoneExtension),
            phoneNumberType: mappedType,
            countryCode: countryCode ? this.getCountryCodeForInput(countryCode) : ''
          });
        });
      } else {
        // Fallback to old structure - support all 5 phone numbers
        const phoneFields = [
          {
            number: 'tempPhoneNumber',
            fallbackNumber: 'phoneNumber',
            extension: 'tempPhoneExtension',
            fallbackExtension: 'phoneExtension',
            type: 'tempPhoneNumberType',
            fallbackType: 'phoneNumberType',
            countryCode: 'tempCountryCode',
            fallbackCountryCode: 'countryCode'
          },
          {
            number: 'tempSecondaryPhoneNumber',
            fallbackNumber: 'secondaryPhoneNumber',
            extension: 'tempSecondaryPhoneExtension',
            fallbackExtension: 'secondaryPhoneExtension',
            type: 'tempSecondaryPhoneNumberType',
            fallbackType: 'secondaryPhoneNumberType',
            countryCode: 'tempSecondaryPhoneCountryCode',
            fallbackCountryCode: 'tempCountryCode'
          },
          {
            number: 'tempThirdPhoneNumber',
            fallbackNumber: 'thirdPhoneNumber',
            extension: 'tempThirdPhoneExtension',
            fallbackExtension: 'thirdPhoneExtension',
            type: 'tempThirdPhoneNumberType',
            fallbackType: 'thirdPhoneNumberType',
            countryCode: 'tempThirdPhoneCountryCode',
            fallbackCountryCode: 'tempCountryCode'
          },
          {
            number: 'tempFourthPhoneNumber',
            fallbackNumber: 'fourthPhoneNumber',
            extension: 'tempFourthPhoneExtension',
            fallbackExtension: 'fourthPhoneExtension',
            type: 'tempFourthPhoneNumberType',
            fallbackType: 'fourthPhoneNumberType',
            countryCode: 'tempFourthPhoneCountryCode',
            fallbackCountryCode: 'tempCountryCode'
          },
          {
            number: 'tempFifthPhoneNumber',
            fallbackNumber: 'fifthPhoneNumber',
            extension: 'tempFifthPhoneExtension',
            fallbackExtension: 'fifthPhoneExtension',
            type: 'tempFifthPhoneNumberType',
            fallbackType: 'fifthPhoneNumberType',
            countryCode: 'tempFifthPhoneCountryCode',
            fallbackCountryCode: 'tempCountryCode'
          }
        ];
        
        phoneFields.forEach(field => {
          const phoneNumber = contactAny[field.number] || contactAny[field.fallbackNumber];
          if (phoneNumber && phoneNumber.trim() !== '') {
            const phoneType = contactAny[field.type] || contactAny[field.fallbackType] || '';
            // Get country code - use specific field if available, otherwise fallback, but no default
            let countryCode = contactAny[field.countryCode] || contactAny[field.fallbackCountryCode] || contactAny.countryCode || '';
            const mappedType = phoneType ? this.mapPhoneType(phoneType) : '';
            phoneNumbers.push({
              phoneNumber: phoneNumber,
              phoneExtension: extensionForWorkPhoneType(
                mappedType,
                contactAny[field.extension] || contactAny[field.fallbackExtension] || ''
              ),
              phoneNumberType: mappedType,
              countryCode: countryCode ? this.getCountryCodeForInput(countryCode) : ''
            });
          }
        });
      }

      // If no phone numbers, add one empty one
      if (phoneNumbers.length === 0) {
        phoneNumbers.push({
          phoneNumber: '',
          phoneExtension: '',
          phoneNumberType: '',
          countryCode: ''
        });
      }

      const firstName = contactAny.tempFirstName || contactAny.firstName || '';
      const lastName = contactAny.tempLastName || contactAny.lastName || '';
      
      return {
        participantId: contactAny.participantId,
        contactId: contactAny.contactId || null, // Store contactId from original data
        firstName: firstName,
        lastName: lastName,
        email: contactAny.tempEmail || contactAny.email || '',
        affiliation: contactAny.affiliation || '',
        phoneNumbers: phoneNumbers,
        countryCode: contactAny.tempCountryCode || contactAny.countryCode ? this.getCountryCodeForInput(contactAny.tempCountryCode || contactAny.countryCode) : '',
        isPrimary: contactAny.isPrimary || false,
        isNew: false // Existing contacts are not new
      };
    });

    // If no contacts exist, add at least one empty contact
    if (this.editContacts.length === 0) {
      const leadFirstName = this.lead?.firstName || '';
      const leadLastName = this.lead?.lastName || '';
      this.editContacts.push({
        firstName: leadFirstName,
        lastName: leadLastName,
        email: this.lead?.email || '',
        affiliation: '',
        phoneNumbers: [{
          phoneNumber: this.lead?.phoneNumber || '',
          phoneExtension: this.lead?.phoneExtension || '',
          phoneNumberType: '',
          countryCode: ''
        }],
        countryCode: '',
        isPrimary: true,
        isNew: false,
        contactId: null
      });
    }
  }

  addNewContact(): void {
    this.editContacts.push({
      firstName: '',
      lastName: '',
      email: '',
      affiliation: '',
      phoneNumbers: [{
        phoneNumber: '',
        phoneExtension: '',
        phoneNumberType: '',
        countryCode: ''
      }],
      countryCode: '',
      isPrimary: false,
      isNew: true, // New contacts are marked as new
      contactId: null
    });
  }

  removeContact(index: number): void {
    if (this.editContacts.length > 1) {
      this.editContacts.splice(index, 1);
      // Ensure at least one contact is primary
      if (this.editContacts.length > 0 && !this.editContacts.some(c => c.isPrimary)) {
        this.editContacts[0].isPrimary = true;
      }
    }
  }

  setPrimaryContact(index: number): void {
    // Set all to false first
    this.editContacts.forEach(contact => contact.isPrimary = false);
    // Set the selected one to true
    this.editContacts[index].isPrimary = true;
  }

  /** Handle Primary Contact toggle: when checked set this contact primary; when unchecked set first contact primary. */
  onPrimaryContactToggle(index: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked) {
      this.setPrimaryContact(index);
    } else {
      this.setPrimaryContact(0);
    }
  }

  getPrimaryContact(): any {
    if (this.lead?.participants && this.lead.participants.length > 0) {
      return this.lead.participants.find(p => p.isPrimary) || this.lead.participants[0];
    }
    return null;
  }

  getContacts() {
    if (!this.lead?.participants || this.lead.participants.length === 0) {
      // Fallback to primary contact from lead
      return [{
        participantId: 0,
        firstName: this.lead?.firstName || '',
        lastName: this.lead?.lastName || '',
        email: this.lead?.email || '',
        phoneNumber: this.lead?.phoneNumber || '',
        phoneExtension: this.lead?.phoneExtension || '',
        secondaryPhoneNumber: this.lead?.secondaryPhoneNumber,
        secondaryPhoneExtension: this.lead?.secondaryPhoneExtension,
        phoneNumberType: 'Primary',
        secondaryPhoneNumberType: 'Secondary',
        countryCode: this.lead?.countryCode || '',
        affiliation: '',
        isPrimary: true
      }];
    }
    // Sort participants consistently: primary first, then by participantId to maintain stable order
    return [...this.lead.participants].sort((a, b) => {
      // Primary participants come first
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      // For non-primary or both primary, sort by participantId to maintain consistent order
      const aId = a.participantId || 0;
      const bId = b.participantId || 0;
      return aId - bId;
    });
  }

  getFullName(contact: any): string {
    const firstName = contact.tempFirstName || contact.firstName || '';
    const lastName = contact.tempLastName || contact.lastName || '';
    return `${firstName} ${lastName}`.trim() || '-';
  }

  getEmail(contact: any): string {
    return contact.tempEmail || contact.email || '-';
  }

  getPhoneNumbers(contact: any): Array<{type: string, number: string, extension: string, countryCode: string}> {
    const phones: Array<{type: string, number: string, extension: string, countryCode: string}> = [];
    
    // First, check if contact has phoneNumbers array (new structure)
    if (contact.phoneNumbers && Array.isArray(contact.phoneNumbers) && contact.phoneNumbers.length > 0) {
      contact.phoneNumbers.forEach((phone: any) => {
        if (phone.phoneNumber && phone.phoneNumber.trim() !== '') {
          // Get phone type - could be phoneType or phoneNumberType, but no default
          const phoneType = phone.phoneType || phone.phoneNumberType || '';
          
          // Get country code - could be phoneCountryCode (full name) or countryCode, but no default
          let countryCode = phone.phoneCountryCode || phone.countryCode || '';
          
          // If it's a 2-letter code, convert to full name
          if (countryCode && countryCode.length <= 2) {
            countryCode = getCountryFullName(countryCode) || '';
          }
          
          phones.push({
            type: this.formatPhoneType(phoneType),
            number: phone.phoneNumber || '',
            extension: extensionForWorkPhoneType(phoneType, phone.phoneExtension),
            countryCode: countryCode
          });
        }
      });
      
      // Return phones from array if we found any
      if (phones.length > 0) {
        return phones;
      }
    }
    
    // Fallback to old structure - support all 5 phone numbers
    const phoneFields = [
      {
        number: 'tempPhoneNumber',
        fallbackNumber: 'phoneNumber',
        extension: 'tempPhoneExtension',
        fallbackExtension: 'phoneExtension',
        type: 'tempPhoneNumberType',
        fallbackType: 'phoneNumberType',
        countryCode: 'tempCountryCode',
        fallbackCountryCode: 'countryCode'
      },
      {
        number: 'tempSecondaryPhoneNumber',
        fallbackNumber: 'secondaryPhoneNumber',
        extension: 'tempSecondaryPhoneExtension',
        fallbackExtension: 'secondaryPhoneExtension',
        type: 'tempSecondaryPhoneNumberType',
        fallbackType: 'secondaryPhoneNumberType',
        countryCode: 'tempSecondaryPhoneCountryCode',
        fallbackCountryCode: 'tempCountryCode'
      },
      {
        number: 'tempThirdPhoneNumber',
        fallbackNumber: 'thirdPhoneNumber',
        extension: 'tempThirdPhoneExtension',
        fallbackExtension: 'thirdPhoneExtension',
        type: 'tempThirdPhoneNumberType',
        fallbackType: 'thirdPhoneNumberType',
        countryCode: 'tempThirdPhoneCountryCode',
        fallbackCountryCode: 'tempCountryCode'
      },
      {
        number: 'tempFourthPhoneNumber',
        fallbackNumber: 'fourthPhoneNumber',
        extension: 'tempFourthPhoneExtension',
        fallbackExtension: 'fourthPhoneExtension',
        type: 'tempFourthPhoneNumberType',
        fallbackType: 'fourthPhoneNumberType',
        countryCode: 'tempFourthPhoneCountryCode',
        fallbackCountryCode: 'tempCountryCode'
      },
      {
        number: 'tempFifthPhoneNumber',
        fallbackNumber: 'fifthPhoneNumber',
        extension: 'tempFifthPhoneExtension',
        fallbackExtension: 'fifthPhoneExtension',
        type: 'tempFifthPhoneNumberType',
        fallbackType: 'fifthPhoneNumberType',
        countryCode: 'tempFifthPhoneCountryCode',
        fallbackCountryCode: 'tempCountryCode'
      }
    ];
    
    phoneFields.forEach(field => {
      const phoneNumber = contact[field.number] || contact[field.fallbackNumber];
      if (phoneNumber && phoneNumber.trim() !== '') {
        const phoneType = contact[field.type] || contact[field.fallbackType] || '';
        // Get country code - use specific field if available, otherwise fallback, but no default
        let countryCode = contact[field.countryCode] || contact[field.fallbackCountryCode] || contact.countryCode || '';
        // If it's a 2-letter code, convert to full name
        if (countryCode && countryCode.length <= 2) {
          countryCode = getCountryFullName(countryCode) || '';
        }
        phones.push({
          type: this.formatPhoneType(phoneType),
          number: phoneNumber,
          extension: extensionForWorkPhoneType(phoneType, contact[field.extension] || contact[field.fallbackExtension] || ''),
          countryCode: countryCode
        });
      }
    });
    
    return phones;
  }

  mapPhoneType(type: string | undefined): string {
    if (!type) return '';
    // Map various phone type formats to our standard format
    const typeLower = type.toLowerCase();
    const typeMap: { [key: string]: string } = {
      'primary': 'mobile',
      'secondary': 'mobile',
      'mobile': 'mobile',
      'cell': 'mobile',
      'home': 'home',
      'work': 'work',
      'other': 'other',
      'office': 'other',
      'fax': 'other'
    };
    return typeMap[typeLower] || '';
  }

  formatPhoneType(type: string): string {
    if (!type || !String(type).trim()) return '-';

    // Map lowercase types to display names
    const typeLower = type.toLowerCase();
    const typeMap: { [key: string]: string } = {
      'cell': 'Mobile',
      'home': 'Home',
      'work': 'Work',
      'other': 'Other',
      'primary': 'Mobile',
      'secondary': 'Mobile',
      'mobile': 'Mobile',
      'third': 'Mobile',
      'fourth': 'Mobile',
      'fifth': 'Mobile',
      'office': 'Other',
      'fax': 'Other'
    };
    return typeMap[typeLower] || type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  }

  getCountryCodeForInput(code: string | undefined): string {
    if (!code) return '';
    // If it's already a full country name, return it
    if (code.length > 2) return code;
    // Otherwise convert code to full name
    return getCountryFullName(code) || '';
  }

  onPhoneTypeChange(contactIndex: number, phoneIndex: number, newType: string | null | undefined): void {
    const phone = this.editContacts[contactIndex]?.phoneNumbers?.[phoneIndex];
    if (!phone) return;
    if ((newType ?? '').toString().trim().toLowerCase() !== 'work') {
      phone.phoneExtension = '';
    }
  }

  addPhoneNumber(contactIndex: number): void {
    const contact = this.editContacts[contactIndex];
    if (!contact) return;

    // Check if contact already has 5 phone numbers
    if (contact.phoneNumbers.length >= 5) {
      this.showPhoneLimitNotification();
      return;
    }

    // Add a new phone number entry
    contact.phoneNumbers.push({
      phoneNumber: '',
      phoneExtension: '',
      phoneNumberType: '',
      countryCode: ''
    });
  }

  removePhoneNumber(contactIndex: number, phoneIndex: number): void {
    const contact = this.editContacts[contactIndex];
    if (!contact || !contact.phoneNumbers) return;

    // Don't allow removing if there's only one phone number
    if (contact.phoneNumbers.length <= 1) return;

    contact.phoneNumbers.splice(phoneIndex, 1);
  }

  showPhoneLimitNotification(): void {
    this.showPhoneLimitMessage = true;
    
    // Clear existing timeout if any
    if (this.phoneLimitMessageTimeout) {
      clearTimeout(this.phoneLimitMessageTimeout);
    }

    // Hide after 3 seconds
    this.phoneLimitMessageTimeout = setTimeout(() => {
      this.showPhoneLimitMessage = false;
    }, 3000);
  }

  getAffiliation(contact: any): string {
    return contact.affiliation || '-';
  }

  formatPhoneNumber(number: string, extension: string, countryCode?: string): string {
    if (!number) return '-';
    
    // Get phone prefix from country code
    let phonePrefix = '';
    if (countryCode) {
      // If countryCode is a 2-letter code, convert to full name first
      let countryFullName = countryCode;
      if (countryCode && countryCode.length <= 2) {
        countryFullName = getCountryFullName(countryCode) || '';
      }
      phonePrefix = getCountryPhoneCode(countryFullName);
    } else {
      // Default to US if no country code provided
      phonePrefix = '+1';
    }
    
    // Remove any non-digit characters from the number
    const digitsOnly = number.replace(/\D/g, '');
    
    // Format the number based on length
    let formattedNumber = '';
    if (digitsOnly.length === 10) {
      // Format as (XXX) XXX-XXXX
      formattedNumber = `(${digitsOnly.substring(0, 3)}) ${digitsOnly.substring(3, 6)}-${digitsOnly.substring(6)}`;
    } else if (digitsOnly.length === 9) {
      // Format as (XXX) XXX-XXX (for 9-digit numbers)
      formattedNumber = `(${digitsOnly.substring(0, 3)}) ${digitsOnly.substring(3, 6)}-${digitsOnly.substring(6)}`;
    } else {
      // For other lengths, just use the number as-is
      formattedNumber = digitsOnly;
    }
    
    // Combine prefix with formatted number
    const formatted = extension && extension.trim() 
      ? `${phonePrefix} ${formattedNumber} ext. ${extension}` 
      : `${phonePrefix} ${formattedNumber}`;
    
    return formatted;
  }

  getCountryCodePrefix(countryCode?: string): string {
    if (!countryCode) return '+1';
    
    // If countryCode is a 2-letter code, convert to full name first
    let countryFullName = countryCode;
    if (countryCode && countryCode.length <= 2) {
      countryFullName = getCountryFullName(countryCode) || '';
    }
    const phonePrefix = getCountryPhoneCode(countryFullName);
    return phonePrefix || '+1';
  }

  formatPhoneNumberOnly(number: string, extension: string): string {
    if (!number) return '-';
    
    // Remove any non-digit characters from the number
    const digitsOnly = number.replace(/\D/g, '');
    
    // Format the number based on length
    let formattedNumber = '';
    if (digitsOnly.length === 10) {
      // Format as (XXX) XXX-XXXX
      formattedNumber = `(${digitsOnly.substring(0, 3)}) ${digitsOnly.substring(3, 6)}-${digitsOnly.substring(6)}`;
    } else if (digitsOnly.length === 9) {
      // Format as (XXX) XXX-XXX (for 9-digit numbers)
      formattedNumber = `(${digitsOnly.substring(0, 3)}) ${digitsOnly.substring(3, 6)}-${digitsOnly.substring(6)}`;
    } else {
      // For other lengths, just use the number as-is
      formattedNumber = digitsOnly;
    }
    
    // Add extension if present
    if (extension && extension.trim()) {
      formattedNumber += ` ext. ${extension}`;
    }
    
    return formattedNumber;
  }

  toggleExpand(contactIndex: number): void {
    if (this.expandedContacts.has(contactIndex)) {
      this.expandedContacts.delete(contactIndex);
    } else {
      this.expandedContacts.add(contactIndex);
    }
  }

  isContactExpanded(contactIndex: number): boolean {
    return this.expandedContacts.has(contactIndex);
  }

  shouldShowDivider(index: number): boolean {
    // Always show divider between contacts (except before the first one)
    // Show it for any contact that has a previous contact, even in collapsed state
    const contacts = this.getContacts();
    return index > 0 && contacts.length > 1;
  }

  formatPhoneNumberForInput(phoneNumber: string): string {
    return formatPhoneNumberForInputDisplay(phoneNumber);
  }

  readonly phoneCountries: AioPhoneCountry[] = COUNTRY_CODE_OPTIONS.map((country) => ({
    id: country.value,
    label: country.label,
    code: country.code ?? '',
    dialCode: country.phoneCode ?? '',
    flag: country.flag ?? '',
  }));

  onPhoneCountryChange(contactIndex: number, phoneIndex: number, country: string): void {
    const phone = this.editContacts[contactIndex]?.phoneNumbers?.[phoneIndex];
    if (phone) phone.countryCode = country;
  }

  onPhoneValueChange(contactIndex: number, phoneIndex: number, text: string): void {
    const phone = this.editContacts[contactIndex]?.phoneNumbers?.[phoneIndex];
    if (phone) phone.phoneNumber = normalizeUsPhoneInputDigits(text);
  }

  onPhoneExtensionChange(contactIndex: number, phoneIndex: number, extension: string): void {
    const phone = this.editContacts[contactIndex]?.phoneNumbers?.[phoneIndex];
    if (phone) phone.phoneExtension = extension;
  }

  // Phone type options for dropdown
  get phoneTypeOptions(): any[] {
    return [
      { label: 'Mobile', value: 'mobile' },
      { label: 'Home', value: 'home' },
      { label: 'Work', value: 'work' },
      { label: 'Other', value: 'other' }
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If lead is updated externally (after save), reset editing state
    if (changes['lead'] && !changes['lead'].firstChange && this.isEditing) {
      // Lead was updated, assume save was successful, reset editing state
      this.isEditing = false;
      this.isExpanded = false; // Collapse card after save
      // Clear original participant IDs tracking
      this.originalParticipantIds.clear();
    }
  }

  ngOnDestroy(): void {
    // Clean up timeout if component is destroyed
    if (this.phoneLimitMessageTimeout) {
      clearTimeout(this.phoneLimitMessageTimeout);
    }
  }
}
